/**
 * Crosstab Tab Content (v2.3)
 *
 * useTabStateフックによる状態永続化（pane split対応）
 */

import React, { useCallback, useMemo, useState } from 'react';
import type { TabState } from '../../../../types';
import { createDatasetId, getFromDatasetRecord, createReportId, createReportElementId, hasData } from '../../../../types';
import { useProjectData } from '../../../stores/hooks/useProjectData';
import { CrosstabEngine } from '../../../utils/crosstab/engine';
import {
  COUNT_FIELD_ID,
  type CrosstabConfiguration,
  type FieldDescriptor,
  type ValueFieldDescriptor,
  type AggregationFunction,
  type CrosstabTabConfig,
  type CrosstabOptions,
  type SortMethod,
  type SortOrder
} from '../../../utils/crosstab/types';
import { CrosstabPreview } from './components/CrosstabPreview';
import { useTabState } from './hooks/useTabState';
import { convertCrosstabToDerivedDataset } from '../../../utils/crosstab/saveAsDataset';
import { evaluateDerivedDataset } from '../../../utils/evaluateDataset';
import logger from '../../../utils/logger';
import * as styles from './CrosstabTabContent.css';
import { ReportModal } from '../../graph-builder/ReportModal';
import { Dialog } from '../../common/Dialog';
import { Button } from '../../common/Button';
import type { Report, CrosstabElement } from '../../../../types/report.types';
import { appendElementReference } from '../../../utils/reportMarkdownParser';

interface CrosstabTabContentProps {
  tab: TabState;
}

export const CrosstabTabContent: React.FC<CrosstabTabContentProps> = ({ tab }) => {
  const { getAllDatasets, project, updateProject } = useProjectData();

  // useTabStateで状態管理（pane split対応）
  const [config, setConfig] = useTabState<CrosstabTabConfig>(tab, {
    datasetId: undefined,
    rowFields: [],
    columnFields: [],
    valueFields: [],
    options: {
      showRowTotals: true,
      showColumnTotals: true,
      showGrandTotal: true,
      emptyDisplayMode: 'blank',
      percentageMode: 'none',
      visualMode: 'table',
      colorScheme: 'blue'
    },
    sortConfig: undefined,
    tabState: {
      cachedResult: undefined
    }
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'data' | 'display'>('data');

  // レポート選択の状態
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [reportModalMode, setReportModalMode] = useState<'select' | 'create'>('select');

  // Save as Dataset modal state
  const [showSaveAsDatasetModal, setShowSaveAsDatasetModal] = useState(false);
  const [datasetNameInput, setDatasetNameInput] = useState('');

  // cachedResultをconfigから取得（pane split時も保持される）
  // 後方互換性: tabState.cachedResult または 旧cachedResultを使用
  const crosstabResult = config.tabState?.cachedResult ?? config.cachedResult ?? null;

  // 結果が古いかどうかを判定
  const isResultStale = useMemo(() => {
    if (!crosstabResult) return false;

    // キャッシュされた結果の設定と現在の設定を比較
    const cached = crosstabResult.configuration;
    const cachedDatasetId = crosstabResult.metadata.datasetId;
    const current = config;

    // データセットが異なる
    if (cachedDatasetId !== current.datasetId) return true;

    // rowFieldsが異なる
    const cachedRowFields = cached.rowFields.map(f => f.fieldName).sort();
    const currentRowFields = [...current.rowFields].sort();
    if (JSON.stringify(cachedRowFields) !== JSON.stringify(currentRowFields)) return true;

    // columnFieldsが異なる
    const cachedColFields = cached.columnFields.map(f => f.fieldName).sort();
    const currentColFields = [...current.columnFields].sort();
    if (JSON.stringify(cachedColFields) !== JSON.stringify(currentColFields)) return true;

    // valueFieldsが異なる
    const cachedValueFields = cached.valueFields.map(f => ({ field: f.fieldName, agg: f.aggregation }));
    const currentValueFields = current.valueFields.map(f => ({ field: f.field, agg: f.aggregation }));
    if (JSON.stringify(cachedValueFields) !== JSON.stringify(currentValueFields)) return true;

    // オプションが異なる（一部のみチェック：集計結果に影響するもの）
    if (cached.options.showRowTotals !== current.options.showRowTotals) return true;
    if (cached.options.showColumnTotals !== current.options.showColumnTotals) return true;
    if (cached.options.showGrandTotal !== current.options.showGrandTotal) return true;

    return false;
  }, [crosstabResult, config]);

  // データセット一覧
  const datasets = getAllDatasets();

  // 選択されたデータセット
  const selectedDataset = useMemo(() => {
    if (!config.datasetId) return undefined;
    return getFromDatasetRecord(project?.datasets || {}, config.datasetId);
  }, [project?.datasets, config.datasetId]);

  // データセット選択
  const handleDatasetChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newDatasetId = e.target.value;
      setConfig({
        datasetId: newDatasetId ? createDatasetId(newDatasetId) : undefined,
        rowFields: [],
        columnFields: [],
        valueFields: [],
        options: config.options,
        tabState: { cachedResult: undefined } // データセット変更時は結果もクリア
      });
    },
    [config.options, setConfig]
  );

  // 利用可能なカラム
  const availableColumns = useMemo(() => {
    return selectedDataset?.schema?.columns || [];
  }, [selectedDataset]);

  // 数値型カラムのみ（値フィールド用）
  const numericColumns = useMemo(() => {
    return availableColumns.filter(col => {
      const type = col.type.toLowerCase();
      return (
        type.includes('int') ||
        type.includes('float') ||
        type.includes('double') ||
        type.includes('boolean')
      );
    });
  }, [availableColumns]);

  // Crosstab生成
  const handleGenerate = useCallback(async () => {
    if (!selectedDataset || !config.datasetId) {
      setError('Please select a dataset');
      return;
    }

    if (config.rowFields.length === 0) {
      setError('Please add at least one row field');
      return;
    }

    if (config.columnFields.length === 0) {
      setError('Please add at least one column field');
      return;
    }

    if (config.valueFields.length === 0) {
      setError('Please add at least one value field');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // FieldDescriptorを構築
      const rowFieldDescriptors: FieldDescriptor[] = config.rowFields.map(fieldName => {
        const column = availableColumns.find(c => c.name === fieldName);
        return {
          fieldId: column!.id,
          fieldName,
          dataType: column!.type,
          sortOrder: 'data'
        };
      });

      const columnFieldDescriptors: FieldDescriptor[] = config.columnFields.map(fieldName => {
        const column = availableColumns.find(c => c.name === fieldName);
        return {
          fieldId: column!.id,
          fieldName,
          dataType: column!.type,
          sortOrder: 'data'
        };
      });

      const valueFieldDescriptors: ValueFieldDescriptor[] = config.valueFields.map(vf => {
        // Countのみの場合は特殊処理
        if (vf.field === COUNT_FIELD_ID) {
          return {
            fieldId: undefined,
            fieldName: '(Count)',
            aggregation: 'count' as AggregationFunction,
            alias: vf.alias,
            dataType: 'int64'
          };
        }
        const column = availableColumns.find(c => c.name === vf.field);
        return {
          fieldId: column!.id,
          fieldName: vf.field,
          aggregation: vf.aggregation,
          alias: vf.alias,
          dataType: column!.type
        };
      });

      const configuration: CrosstabConfiguration = {
        rowFields: rowFieldDescriptors,
        columnFields: columnFieldDescriptors,
        valueFields: valueFieldDescriptors,
        options: config.options
      };

      // Ensure dataset has data
      let datasetWithData;
      if (!hasData(selectedDataset)) {
        if (selectedDataset.type === 'derived') {
          datasetWithData = await evaluateDerivedDataset(selectedDataset, project?.datasets || {});
        } else {
          // This shouldn't happen for primary/ephemeral, but handle it
          setError('Dataset does not have data loaded');
          return;
        }
      } else {
        datasetWithData = selectedDataset;
      }

      const engine = new CrosstabEngine();
      const result = await engine.execute(datasetWithData, configuration);

      // 結果をconfigに保存（pane split時も保持される）
      setConfig(prev => ({ ...prev, tabState: { cachedResult: result } }));
      logger.info('[CrosstabTabContent] Crosstab generated successfully');
    } catch (err) {
      logger.error('[CrosstabTabContent] Error generating crosstab:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate crosstab');
    } finally {
      setIsGenerating(false);
    }
  }, [selectedDataset, config, availableColumns, setConfig, project]);

  // リセット
  const handleReset = useCallback(() => {
    setConfig(prev => ({
      ...prev,
      rowFields: [],
      columnFields: [],
      valueFields: [],
      tabState: { cachedResult: undefined } // リセット時は結果もクリア
    }));
    setError(null);
  }, [setConfig]);

  // ソート設定変更
  const handleSortMethodChange = useCallback(
    (target: 'rows' | 'columns', method: SortMethod | '') => {
      if (!method) {
        // Original Orderに戻す
        setConfig(prev => ({
          ...prev,
          sortConfig: {
            ...prev.sortConfig,
            [target]: undefined
          }
        }));
      } else if (method === 'value') {
        // Valueソート: デフォルトでtotal、降順
        setConfig(prev => ({
          ...prev,
          sortConfig: {
            ...prev.sortConfig,
            [target]: { method: 'value', sortByKey: 'total', order: 'desc' }
          }
        }));
      } else {
        // Labelソート
        setConfig(prev => ({
          ...prev,
          sortConfig: {
            ...prev.sortConfig,
            [target]: { method }
          }
        }));
      }
    },
    [setConfig]
  );

  // 値ソートのキー変更
  const handleSortByKeyChange = useCallback(
    (target: 'rows' | 'columns', sortByKey: string) => {
      setConfig(prev => ({
        ...prev,
        sortConfig: {
          ...prev.sortConfig,
          [target]: {
            ...(prev.sortConfig?.[target] || { method: 'value', order: 'desc' }),
            sortByKey
          }
        }
      }));
    },
    [setConfig]
  );

  // 値ソートの順序変更
  const handleSortOrderChange = useCallback(
    (target: 'rows' | 'columns', order: SortOrder) => {
      setConfig(prev => ({
        ...prev,
        sortConfig: {
          ...prev.sortConfig,
          [target]: {
            ...(prev.sortConfig?.[target] || { method: 'value', sortByKey: 'total' }),
            order
          }
        }
      }));
    },
    [setConfig]
  );

  // 新規レポート作成
  const createNewReport = useCallback((name: string, description: string): Report => {
    logger.info('[Crosstab] createNewReport called', { name, description });

    const newReport: Report = {
      id: createReportId(`report-${crypto.randomUUID()}`),
      name: name || `Crosstab Report - ${new Date().toLocaleDateString()}`,
      description: description || 'Created from Crosstab',
      content: '',
      elements: {},
      reportSettings: {
        contentWidth: 800,
        defaultElementMargin: 16,
        backgroundColor: '#ffffff',
        autoSave: false,
        autoSaveInterval: 5
      },
      metadata: {
        createdAt: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        createdBy: 'Crosstab',
        sharing: {
          isPublic: false,
          permissions: ['view', 'edit'] as const
        }
      }
    };

    updateProject((proj) => {
      return {
        ...proj,
        reports: [...(proj.reports || []), newReport]
      };
    });

    logger.info('[Crosstab] Created new report', { reportId: newReport.id });
    return newReport;
  }, [updateProject]);

  // クロス集計をレポートに追加
  const addCrosstabToReport = useCallback((reportId: string) => {
    if (!config.datasetId || !selectedDataset) return;

    // FieldDescriptorを構築（handleGenerateと同じロジック）
    const rowFieldDescriptors: FieldDescriptor[] = config.rowFields.map(fieldName => {
      const column = availableColumns.find(c => c.name === fieldName);
      return {
        fieldId: column!.id,
        fieldName,
        dataType: column!.type,
        sortOrder: 'data'
      };
    });

    const columnFieldDescriptors: FieldDescriptor[] = config.columnFields.map(fieldName => {
      const column = availableColumns.find(c => c.name === fieldName);
      return {
        fieldId: column!.id,
        fieldName,
        dataType: column!.type,
        sortOrder: 'data'
      };
    });

    const valueFieldDescriptors: ValueFieldDescriptor[] = config.valueFields.map(vf => {
      // Countのみの場合は特殊処理
      if (vf.field === COUNT_FIELD_ID) {
        return {
          fieldId: undefined,
          fieldName: '(Count)',
          aggregation: 'count' as AggregationFunction,
          alias: vf.alias,
          dataType: 'int64'
        };
      }
      const column = availableColumns.find(c => c.name === vf.field);
      return {
        fieldId: column!.id,
        fieldName: vf.field,
        aggregation: vf.aggregation,
        alias: vf.alias,
        dataType: column!.type
      };
    });

    const configuration: CrosstabConfiguration = {
      rowFields: rowFieldDescriptors,
      columnFields: columnFieldDescriptors,
      valueFields: valueFieldDescriptors,
      options: config.options
    };

    const crosstabElement: CrosstabElement = {
      id: createReportElementId(`crosstab-${crypto.randomUUID()}`),
      type: 'crosstab',
      crosstabConfiguration: configuration,
      datasetId: config.datasetId,
      title: `Crosstab - ${selectedDataset.name}`,
      height: 400,
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };

    updateProject((proj) => {
      const updatedReports = (proj.reports || []).map(report => {
        if (report.id === reportId) {
          return {
            ...report,
            content: appendElementReference(report.content, crosstabElement.type, crosstabElement.id),
            elements: {
              ...report.elements,
              [crosstabElement.id]: crosstabElement
            },
            metadata: {
              ...report.metadata,
              lastModified: new Date().toISOString()
            }
          };
        }
        return report;
      });

      return {
        ...proj,
        reports: updatedReports
      };
    });

    logger.info('[Crosstab] Added crosstab to report', { reportId, elementId: crosstabElement.id });
  }, [config, selectedDataset, availableColumns, updateProject]);

  // レポートモーダルを表示
  const handleAddToReport = useCallback(() => {
    if (!crosstabResult) {
      logger.warn('[Crosstab] Cannot add to report: no crosstab result');
      return;
    }

    const reports = project?.reports || [];
    // レポートが存在する場合は選択モード、存在しない場合は作成モードで開始
    setReportModalMode(reports.length > 0 ? 'select' : 'create');
    setShowReportModal(true);
  }, [crosstabResult, project]);

  // レポートモーダルを閉じる
  const handleReportModalClose = useCallback(() => {
    setShowReportModal(false);
  }, []);

  // レポートモーダルでの確定処理
  const handleReportModalConfirm = useCallback((data: { reportId?: string; newReportName?: string; newReportDescription?: string }) => {
    logger.info('[Crosstab] handleReportModalConfirm called', { data });

    if (data.reportId) {
      // 既存レポートに追加
      addCrosstabToReport(data.reportId);
    } else if (data.newReportName) {
      // 新規レポートを作成してクロス集計を追加
      const newReport = createNewReport(data.newReportName, data.newReportDescription || '');
      addCrosstabToReport(newReport.id);
    }

    // モーダルを閉じてリセット
    setShowReportModal(false);
    setSelectedReportId(null);
  }, [addCrosstabToReport, createNewReport]);

  // Save as Dataset - open modal
  const handleOpenSaveAsDatasetModal = useCallback(() => {
    if (!crosstabResult) {
      logger.warn('[Crosstab] Cannot save as dataset: no result');
      return;
    }

    // デフォルトのデータセット名を生成
    const defaultName = `Crosstab: ${config.rowFields.join(', ')} × ${config.columnFields.join(', ')}`;
    setDatasetNameInput(defaultName);
    setShowSaveAsDatasetModal(true);
  }, [crosstabResult, config.rowFields, config.columnFields]);

  // Save as Dataset - confirm
  const handleConfirmSaveAsDataset = useCallback(() => {
    if (!crosstabResult || !datasetNameInput.trim()) {
      logger.warn('[Crosstab] Cannot save as dataset: no result or empty name');
      return;
    }

    // Derived Datasetに変換
    const derivedDataset = convertCrosstabToDerivedDataset(crosstabResult, datasetNameInput.trim());

    // プロジェクトに追加
    updateProject(proj => {
      if (!proj) return proj;

      logger.info('[Crosstab] Saving crosstab as dataset', { datasetId: derivedDataset.id, name: datasetNameInput.trim() });

      return {
        ...proj,
        datasets: {
          ...proj.datasets,
          [derivedDataset.id]: derivedDataset
        }
      };
    });

    logger.info('[Crosstab] Crosstab saved as dataset successfully');
    setShowSaveAsDatasetModal(false);
  }, [crosstabResult, datasetNameInput, updateProject]);

  // Save as Dataset - cancel
  const handleCancelSaveAsDataset = useCallback(() => {
    setShowSaveAsDatasetModal(false);
  }, []);

  return (
    <div className={styles.container}>
      {/* Configuration Panel */}
      <div className={styles.configPanel}>
        {/* Tab Header */}
        <div className={styles.tabHeader}>
          <button
            className={`${styles.tab} ${activeTab === 'data' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('data')}
          >
            Data
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'display' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('display')}
          >
            Display
          </button>
        </div>

        {/* Data Tab */}
        {activeTab === 'data' && (
          <div className={styles.tabContent}>
            {/* Dataset Selector */}
        <div className={styles.fieldGroup}>
          <label htmlFor={`dataset-${tab.id}`}>Dataset</label>
          <select
            id={`dataset-${tab.id}`}
            value={config.datasetId || ''}
            onChange={handleDatasetChange}
            className={styles.select}
          >
            <option value="">Select a dataset...</option>
            {datasets.map(dataset => (
              <option key={dataset.id} value={dataset.id}>
                {dataset.name} ({dataset.rowCount || 0} rows)
              </option>
            ))}
          </select>
        </div>

        {selectedDataset && (
          <>
            {/* Row Fields */}
            <div className={styles.fieldGroup}>
              <label>Rows</label>
              <div className={styles.fieldList}>
                {config.rowFields.map((field, index) => (
                  <div key={index} className={styles.selectedField}>
                    <span>{field}</span>
                    <button
                      onClick={() =>
                        setConfig(prev => ({
                          ...prev,
                          rowFields: prev.rowFields.filter((_, i) => i !== index)
                        }))
                      }
                      className={styles.removeButton}
                      aria-label="Remove field"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <select
                  value=""
                  onChange={e => {
                    if (e.target.value) {
                      setConfig(prev => ({
                        ...prev,
                        rowFields: [...prev.rowFields, e.target.value]
                      }));
                    }
                  }}
                  className={styles.addFieldSelect}
                >
                  <option value="">Add field...</option>
                  {availableColumns
                    .filter(
                      col =>
                        !config.rowFields.includes(col.name) &&
                        !config.columnFields.includes(col.name)
                    )
                    .map(col => (
                      <option key={col.id} value={col.name}>
                        {col.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {/* Column Fields */}
            <div className={styles.fieldGroup}>
              <label>Columns</label>
              <div className={styles.fieldList}>
                {config.columnFields.map((field, index) => (
                  <div key={index} className={styles.selectedField}>
                    <span>{field}</span>
                    <button
                      onClick={() =>
                        setConfig(prev => ({
                          ...prev,
                          columnFields: prev.columnFields.filter((_, i) => i !== index)
                        }))
                      }
                      className={styles.removeButton}
                      aria-label="Remove field"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <select
                  value=""
                  onChange={e => {
                    if (e.target.value) {
                      setConfig(prev => ({
                        ...prev,
                        columnFields: [...prev.columnFields, e.target.value]
                      }));
                    }
                  }}
                  className={styles.addFieldSelect}
                >
                  <option value="">Add field...</option>
                  {availableColumns
                    .filter(
                      col =>
                        !config.rowFields.includes(col.name) &&
                        !config.columnFields.includes(col.name)
                    )
                    .map(col => (
                      <option key={col.id} value={col.name}>
                        {col.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {/* Value Fields */}
            <div className={styles.fieldGroup}>
              <label>Values</label>
              <div className={styles.fieldList}>
                {config.valueFields.map((valueField, index) => (
                  <div key={index} className={styles.valueField}>
                    <span>{valueField.field === COUNT_FIELD_ID ? '(Count)' : valueField.field}</span>
                    {/* Countの場合は集計関数を固定、それ以外は変更可能 */}
                    {valueField.field === COUNT_FIELD_ID ? (
                      <span className={styles.aggregationLabel}>COUNT</span>
                    ) : (
                      <select
                        value={valueField.aggregation}
                        onChange={e => {
                          setConfig(prev => {
                            const newValueFields = [...prev.valueFields];
                            newValueFields[index] = {
                              ...valueField,
                              aggregation: e.target.value as AggregationFunction
                            };
                            return { ...prev, valueFields: newValueFields };
                          });
                        }}
                        className={styles.aggregationSelect}
                      >
                        <option value="sum">SUM</option>
                        <option value="avg">AVG</option>
                        <option value="count">COUNT</option>
                        <option value="min">MIN</option>
                        <option value="max">MAX</option>
                      </select>
                    )}
                    <button
                      onClick={() =>
                        setConfig(prev => ({
                          ...prev,
                          valueFields: prev.valueFields.filter((_, i) => i !== index)
                        }))
                      }
                      className={styles.removeButton}
                      aria-label="Remove field"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <select
                  value=""
                  onChange={e => {
                    if (e.target.value) {
                      // Countの場合は集計関数をcountに固定
                      const aggregation = e.target.value === COUNT_FIELD_ID ? 'count' : 'sum';
                      setConfig(prev => ({
                        ...prev,
                        valueFields: [
                          ...prev.valueFields,
                          { field: e.target.value, aggregation: aggregation as AggregationFunction }
                        ]
                      }));
                    }
                  }}
                  className={styles.addFieldSelect}
                >
                  <option value="">Add value field...</option>
                  {/* (Count)オプション - レコード数を集計 */}
                  {!config.valueFields.some(vf => vf.field === COUNT_FIELD_ID) && (
                    <option value={COUNT_FIELD_ID}>(Count)</option>
                  )}
                  {numericColumns
                    .filter(col => !config.valueFields.some(vf => vf.field === col.name))
                    .map(col => (
                      <option key={col.id} value={col.name}>
                        {col.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {/* Options */}
            <div className={styles.optionsSection}>
              <h4 className={styles.sectionTitle}>Options</h4>

              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={config.options.showRowTotals}
                  onChange={e =>
                    setConfig(prev => ({
                      ...prev,
                      options: { ...prev.options, showRowTotals: e.target.checked }
                    }))
                  }
                />
                Show row totals
              </label>

              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={config.options.showColumnTotals}
                  onChange={e =>
                    setConfig(prev => ({
                      ...prev,
                      options: { ...prev.options, showColumnTotals: e.target.checked }
                    }))
                  }
                />
                Show column totals
              </label>

              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={config.options.showGrandTotal}
                  onChange={e =>
                    setConfig(prev => ({
                      ...prev,
                      options: { ...prev.options, showGrandTotal: e.target.checked }
                    }))
                  }
                />
                Show grand total
              </label>

              <div className={styles.optionGroup}>
                <label>Percentage display</label>
                <select
                  value={config.options.percentageMode}
                  onChange={e =>
                    setConfig(prev => ({
                      ...prev,
                      options: {
                        ...prev.options,
                        percentageMode: e.target.value as CrosstabOptions['percentageMode']
                      }
                    }))
                  }
                  className={styles.select}
                >
                  <option value="none">None</option>
                  <option value="row">Row %</option>
                  <option value="column">Column %</option>
                  <option value="total">Total %</option>
                  <option value="all">All</option>
                </select>
              </div>
            </div>

                {/* Action Buttons */}
                <div className={styles.actionButtons}>
                  <button
                    className={styles.generateButton}
                    disabled={
                      config.rowFields.length === 0 ||
                      config.columnFields.length === 0 ||
                      config.valueFields.length === 0 ||
                      isGenerating
                    }
                    onClick={handleGenerate}
                  >
                    {isGenerating ? 'Generating...' : 'Generate Crosstab'}
                  </button>
                  <button className={styles.resetButton} onClick={handleReset}>
                    Reset
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Display Tab */}
        {activeTab === 'display' && (
          <div className={styles.tabContent}>
            {/* Sort Rows */}
            <div className={styles.sortGroup}>
              <div className={styles.sortLabel}>Sort Rows</div>
              <div className={styles.radioGroup}>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="sortRows"
                    value=""
                    checked={!config.sortConfig?.rows}
                    onChange={() => handleSortMethodChange('rows', '')}
                  />
                  Original Order
                </label>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="sortRows"
                    value="label-asc"
                    checked={config.sortConfig?.rows?.method === 'label-asc'}
                    onChange={() => handleSortMethodChange('rows', 'label-asc')}
                  />
                  Label (A-Z)
                </label>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="sortRows"
                    value="label-desc"
                    checked={config.sortConfig?.rows?.method === 'label-desc'}
                    onChange={() => handleSortMethodChange('rows', 'label-desc')}
                  />
                  Label (Z-A)
                </label>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="sortRows"
                    value="value"
                    checked={config.sortConfig?.rows?.method === 'value'}
                    onChange={() => handleSortMethodChange('rows', 'value')}
                  />
                  Value
                </label>

                {/* Value選択時のドロップダウン */}
                {config.sortConfig?.rows?.method === 'value' && crosstabResult && (
                  <div className={styles.sortOptions}>
                    <div className={styles.optionGroup}>
                      <label>Sort by</label>
                      <select
                        value={config.sortConfig.rows.sortByKey || 'total'}
                        onChange={e => handleSortByKeyChange('rows', e.target.value)}
                        className={styles.select}
                      >
                        <option value="total">Total</option>
                        {crosstabResult.structure.columnHeaders
                          .filter((h, i, arr) => arr.findIndex(x => x.key === h.key) === i)
                          .map(header => (
                            <option key={header.key} value={header.key}>
                              {header.displayValues.join(' / ')}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className={styles.optionGroup}>
                      <label>Order</label>
                      <select
                        value={config.sortConfig.rows.order || 'desc'}
                        onChange={e => handleSortOrderChange('rows', e.target.value as SortOrder)}
                        className={styles.select}
                      >
                        <option value="desc">High to Low</option>
                        <option value="asc">Low to High</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sort Columns */}
            <div className={styles.sortGroup}>
              <div className={styles.sortLabel}>Sort Columns</div>
              <div className={styles.radioGroup}>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="sortColumns"
                    value=""
                    checked={!config.sortConfig?.columns}
                    onChange={() => handleSortMethodChange('columns', '')}
                  />
                  Original Order
                </label>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="sortColumns"
                    value="label-asc"
                    checked={config.sortConfig?.columns?.method === 'label-asc'}
                    onChange={() => handleSortMethodChange('columns', 'label-asc')}
                  />
                  Label (A-Z)
                </label>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="sortColumns"
                    value="label-desc"
                    checked={config.sortConfig?.columns?.method === 'label-desc'}
                    onChange={() => handleSortMethodChange('columns', 'label-desc')}
                  />
                  Label (Z-A)
                </label>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="sortColumns"
                    value="value"
                    checked={config.sortConfig?.columns?.method === 'value'}
                    onChange={() => handleSortMethodChange('columns', 'value')}
                  />
                  Value
                </label>

                {/* Value選択時のドロップダウン */}
                {config.sortConfig?.columns?.method === 'value' && crosstabResult && (
                  <div className={styles.sortOptions}>
                    <div className={styles.optionGroup}>
                      <label>Sort by</label>
                      <select
                        value={config.sortConfig.columns.sortByKey || 'total'}
                        onChange={e => handleSortByKeyChange('columns', e.target.value)}
                        className={styles.select}
                      >
                        <option value="total">Total</option>
                        {crosstabResult.structure.rowHeaders
                          .filter((h, i, arr) => arr.findIndex(x => x.key === h.key) === i)
                          .map(header => (
                            <option key={header.key} value={header.key}>
                              {header.displayValues.join(' / ')}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className={styles.optionGroup}>
                      <label>Order</label>
                      <select
                        value={config.sortConfig.columns.order || 'desc'}
                        onChange={e => handleSortOrderChange('columns', e.target.value as SortOrder)}
                        className={styles.select}
                      >
                        <option value="desc">High to Low</option>
                        <option value="asc">Low to High</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Visual Modes */}
            <div className={styles.sortGroup}>
              <div className={styles.sortLabel}>Visual Mode</div>
              {config.valueFields.length > 1 && (
                <div className={styles.disabledNote}>
                  Visual modes are disabled when multiple value fields are selected
                </div>
              )}
              <div className={styles.optionGroup}>
                <label>Display as</label>
                <select
                  value={config.options.visualMode}
                  disabled={config.valueFields.length > 1}
                  onChange={e =>
                    setConfig(prev => ({
                      ...prev,
                      options: {
                        ...prev.options,
                        visualMode: e.target.value as CrosstabOptions['visualMode']
                      }
                    }))
                  }
                  className={styles.select}
                >
                  <option value="table">Table</option>
                  <option value="heatmap">Heatmap</option>
                  <option value="text-heatmap">Text + Heatmap</option>
                  <option value="bar-in-cell">Bar in Cell</option>
                </select>
              </div>

              {/* Color Scheme (表示はvisualMode !== 'table'の時のみ) */}
              {config.options.visualMode !== 'table' && config.valueFields.length === 1 && (
                <div className={styles.optionGroup}>
                  <label>Color scheme</label>
                  <select
                    value={config.options.colorScheme || 'blue'}
                    onChange={e =>
                      setConfig(prev => ({
                        ...prev,
                        options: {
                          ...prev.options,
                          colorScheme: e.target.value as CrosstabOptions['colorScheme']
                        }
                      }))
                    }
                    className={styles.select}
                  >
                    <option value="blue">Blue</option>
                    <option value="red">Red</option>
                    <option value="green">Green</option>
                    <option value="diverging">Diverging (Red-White-Blue)</option>
                  </select>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className={styles.actionButtons}>
              <button
                className={styles.generateButton}
                disabled={
                  config.rowFields.length === 0 ||
                  config.columnFields.length === 0 ||
                  config.valueFields.length === 0 ||
                  isGenerating
                }
                onClick={handleGenerate}
              >
                {isGenerating ? 'Updating...' : 'Update Crosstab'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Area */}
      <div className={styles.mainArea}>
        <div className={styles.previewArea}>
          {error && <div className={styles.error}>Error: {error}</div>}

          {isGenerating && <div className={styles.loading}>Generating crosstab...</div>}

          {!error && !isGenerating && !crosstabResult && (
            <div className={styles.placeholder}>
              Configure fields and click "Generate Crosstab" to see results
            </div>
          )}

          {crosstabResult && !isGenerating && (
            <CrosstabPreview
              result={crosstabResult}
              isStale={isResultStale}
              sortConfig={config.sortConfig}
            />
          )}
        </div>

        {/* Footer - Save actions */}
        <div className={styles.footer}>
          <button
            className={styles.button}
            onClick={handleOpenSaveAsDatasetModal}
            disabled={!crosstabResult || isGenerating}
          >
            Save as Dataset
          </button>
          <button
            className={styles.buttonPrimary}
            onClick={handleAddToReport}
            disabled={!crosstabResult || isGenerating}
          >
            Add to Report
          </button>
        </div>
      </div>

      {/* レポートモーダルコンポーネント */}
      <ReportModal
        isOpen={showReportModal}
        reports={project?.reports || []}
        mode={reportModalMode}
        selectedReportId={selectedReportId}
        onModeChange={setReportModalMode}
        onReportSelect={setSelectedReportId}
        onConfirm={handleReportModalConfirm}
        onClose={handleReportModalClose}
      />

      {/* Save as Dataset Modal */}
      <Dialog
        isOpen={showSaveAsDatasetModal}
        onClose={handleCancelSaveAsDataset}
        title="Save as Dataset"
        footer={
          <>
            <Button variant="secondary" onClick={handleCancelSaveAsDataset}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmSaveAsDataset}
              disabled={!datasetNameInput.trim()}
            >
              Save
            </Button>
          </>
        }
      >
        <div style={{ padding: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px' }}>
            Dataset name:
          </label>
          <input
            type="text"
            value={datasetNameInput}
            onChange={(e) => setDatasetNameInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && datasetNameInput.trim()) {
                handleConfirmSaveAsDataset();
              }
            }}
            style={{
              width: '100%',
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px'
            }}
            autoFocus
          />
        </div>
      </Dialog>
    </div>
  );
};
